import React, { useState } from 'react';
import { Play, CheckCircle, X, Sparkles } from 'lucide-react';
import { trackQuickChallenge, trackStudentCTA } from '../../services/analytics';

const QuickChallenge = ({ isDarkMode, onGetStarted }) => {
  const [challengeStep, setChallengeStep] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [challengeScore, setChallengeScore] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  const challengeQuestions = [
    {
      question: "What is 15% of 240?",
      options: ["32", "36", "38", "42"],
      correct: 1,
      explanation: "To find 15% of 240:\n• Step 1: Convert 15% to decimal = 0.15\n• Step 2: Multiply 0.15 × 240 = 36\n• Answer: 36"
    },
    {
      question: "If a triangle has angles of 45° and 90°, what is the third angle?",
      options: ["35°", "45°", "55°", "65°"],
      correct: 1,
      explanation: "Sum of angles in a triangle = 180°\n• Step 1: Add known angles: 45° + 90° = 135°\n• Step 2: Subtract from 180°: 180° - 135° = 45°\n• Answer: 45°"
    },
    {
      question: "Which word is spelled correctly?",
      options: ["Accomodate", "Accommodate", "Acommodate", "Acomodate"],
      correct: 1,
      explanation: "The correct spelling is 'Accommodate'\n• Root: Latin 'accommodare'\n• Double 'c' and double 'm'\n• Memory tip: It has room to accommodate both double letters!"
    }
  ];

  const handleChallengeAnswer = (index) => {
    if (!hasStarted) {
      setHasStarted(true);
      trackQuickChallenge.started();
    }
    
    setSelectedAnswer(index);
    const isCorrect = index === challengeQuestions[challengeStep].correct;
    
    if (isCorrect) {
      setChallengeScore(prev => prev + 1);
    }
    
    trackQuickChallenge.answerSelected(challengeStep, isCorrect);
  };

  const toggleExplanation = () => {
    const newState = !showExplanation;
    setShowExplanation(newState);
    if (newState) {
      trackQuickChallenge.explanationViewed(challengeStep);
    }
  };

  const nextChallengeQuestion = () => {
    setSelectedAnswer(null);
    setShowExplanation(false);
    if (challengeStep < challengeQuestions.length - 1) {
      setChallengeStep(prev => prev + 1);
    } else {
      trackQuickChallenge.completed(challengeScore, challengeQuestions.length);
    }
  };

  const resetChallenge = () => {
    setChallengeStep(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setChallengeScore(0);
    setHasStarted(false);
  };

  const handleCreateQuiz = () => {
    trackStudentCTA('quick_challenge');
    onGetStarted('student');
  };

  return (
    <section data-testid="section-quick-study-challenge" id="quick-challenge" className={`py-16 md:py-24 ${isDarkMode ? 'bg-gradient-to-br from-purple-900/30 to-violet-900/30' : 'bg-gradient-to-br from-purple-50 to-violet-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold mb-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
            <Play className="w-4 h-4" />
            Try It Now - No Login Required
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
            Quick Study Challenge
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Experience AIQuizPark instantly. Answer 3 sample questions and get AI-powered explanations.
          </p>
        </div>

        <div className={`max-w-2xl mx-auto rounded-2xl p-6 sm:p-8 ${isDarkMode ? 'bg-[#131C2E] border border-slate-800' : 'bg-white border border-slate-200'} shadow-xl`}>
          {/* Progress */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Question {challengeStep + 1} of {challengeQuestions.length}</span>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-bold ${isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
              Score: {challengeScore}/{challengeQuestions.length}
            </div>
          </div>

          {/* Progress Bar */}
          <div className={`h-2 rounded-full mb-6 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
            <div 
              className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-violet-500 transition-all duration-300"
              style={{ width: `${((challengeStep + (selectedAnswer !== null ? 1 : 0)) / challengeQuestions.length) * 100}%` }}
            />
          </div>

          {challengeStep < challengeQuestions.length ? (
            <>
              {/* Question */}
              <div className="mb-6">
                <h3 className="text-lg sm:text-xl font-bold mb-6">{challengeQuestions[challengeStep].question}</h3>
                
                {/* Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {challengeQuestions[challengeStep].options.map((option, i) => {
                    const isSelected = selectedAnswer === i;
                    const isCorrect = i === challengeQuestions[challengeStep].correct;
                    const showResult = selectedAnswer !== null;
                    
                    let buttonClass = `w-full p-4 rounded-xl text-left font-medium transition-all border-2 `;
                    
                    if (showResult) {
                      if (isCorrect) {
                        buttonClass += isDarkMode ? 'bg-green-900/30 border-green-500 text-green-300' : 'bg-green-50 border-green-500 text-green-700';
                      } else if (isSelected && !isCorrect) {
                        buttonClass += isDarkMode ? 'bg-red-900/30 border-red-500 text-red-300' : 'bg-red-50 border-red-500 text-red-700';
                      } else {
                        buttonClass += isDarkMode ? 'bg-slate-800/50 border-slate-700 text-slate-400' : 'bg-slate-50 border-slate-200 text-slate-500';
                      }
                    } else {
                      buttonClass += isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-purple-500 text-slate-200' : 'bg-white border-slate-200 hover:border-purple-400 text-slate-800';
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => !showResult && handleChallengeAnswer(i)}
                        disabled={showResult}
                        data-testid={`challenge-option-${i}`}
                        className={buttonClass}
                      >
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full mr-3 text-sm ${
                          showResult && isCorrect ? (isDarkMode ? 'bg-green-500 text-white' : 'bg-green-500 text-white') :
                          showResult && isSelected && !isCorrect ? (isDarkMode ? 'bg-red-500 text-white' : 'bg-red-500 text-white') :
                          isDarkMode ? 'bg-slate-700' : 'bg-slate-200'
                        }`}>
                          {String.fromCharCode(65 + i)}
                        </span>
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Result & Explanation */}
              {selectedAnswer !== null && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl ${selectedAnswer === challengeQuestions[challengeStep].correct ? (isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200') : (isDarkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50 border border-red-200')}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {selectedAnswer === challengeQuestions[challengeStep].correct ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="font-bold text-green-600 dark:text-green-400">Correct!</span>
                        </>
                      ) : (
                        <>
                          <X className="w-5 h-5 text-red-500" />
                          <span className="font-bold text-red-600 dark:text-red-400">Not quite!</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Toggle Explanation */}
                  <button 
                    onClick={toggleExplanation}
                    data-testid="show-explanation-btn"
                    className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-purple-400' : 'bg-purple-50 hover:bg-purple-100 text-purple-600'}`}
                  >
                    <Sparkles className="w-4 h-4" />
                    {showExplanation ? 'Hide' : 'Show'} Step-by-Step Solution
                  </button>

                  {showExplanation && (
                    <div className={`p-4 rounded-xl font-mono text-sm whitespace-pre-line ${isDarkMode ? 'bg-slate-900 text-slate-300 border border-slate-700' : 'bg-slate-900 text-slate-300'}`}>
                      {challengeQuestions[challengeStep].explanation}
                    </div>
                  )}

                  {/* Next Button */}
                  {challengeStep < challengeQuestions.length - 1 ? (
                    <button 
                      onClick={nextChallengeQuestion}
                      data-testid="next-question-btn"
                      className="w-full py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all"
                    >
                      Next Question →
                    </button>
                  ) : (
                    <button 
                      onClick={handleCreateQuiz}
                      data-testid="create-quiz-cta"
                      className="w-full py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all"
                    >
                      Create Your Own Quiz →
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Challenge Complete */
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold mb-2">Challenge Complete!</h3>
              <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                You scored {challengeScore} out of {challengeQuestions.length}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={resetChallenge} className={`px-6 py-3 rounded-xl font-medium ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                  Try Again
                </button>
                <button onClick={handleCreateQuiz} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-bold">
                  Create Your Own Quiz →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default QuickChallenge;
